/**
 * Lets the synchronous store hooks persist to an asynchronous backend.
 *
 * THE PROBLEM: the three CMS hooks are built around synchronous
 * loadStore()/saveStore() calls, and every store function is pure and sync.
 * Making them async would ripple through every caller, every agent tool, and
 * the HITL queue -- a large, risky change for no user-visible benefit.
 *
 * THE APPROACH: the database is the only store. Nothing is cached locally --
 * the store lives in React state for the life of the page and is pulled
 * fresh on mount and on every team switch.
 *
 * When a load FAILS, writes are blocked until a pull succeeds. Otherwise a
 * debounced save could push whatever half-state was in memory over good
 * server data.
 *
 * WRITE COALESCING: saveStore fires on every keystroke, so pushes are
 * collapsed over a short window (see DEBOUNCE_MS) and only the LATEST store
 * is sent. The window is short enough that a save is not something the user
 * waits for -- it is there to avoid a request per character, not to defer
 * the write.
 */

import type { StoreAdapter } from "./types"

/**
 * Readable text for a thrown value.
 *
 * PostgREST rejects with a plain object ({ code, message, hint, details }),
 * not an Error -- so String(err) yields "[object Object]" and the user is
 * told nothing. Its `message` is the part worth showing, and `hint` often
 * names the fix.
 */
function describeError(err: unknown): string {
  if (err instanceof Error) return err.message
  if (err && typeof err === "object") {
    const e = err as { message?: string; hint?: string; code?: string }
    if (e.message) return e.hint ? `${e.message} (${e.hint})` : e.message
    if (e.code) return `database error ${e.code}`
  }
  return String(err)
}

export type SyncState = "idle" | "syncing" | "error" | "offline"

/**
 * Outcome of a load. `ok: true, store: null` means the team has no rows yet,
 * which is NOT the same as a failure -- see pull().
 */
export type PullResult<TStore> =
  | { ok: true; store: TStore | null; error?: undefined }
  | { ok: false; store?: undefined; error: string }

export interface SyncStatus {
  state: SyncState
  /** When the last successful push completed. */
  lastSyncedAt: number | null
  error: string | null
  /** True when local edits exist that the server has not accepted yet. */
  pending: boolean
}

/*
 * Coalescing window, not a "save later" delay.
 *
 * Text inputs call set() on every keystroke, so writing with no window at all
 * would be one request per character. 150ms is below the threshold where a
 * save feels deferred, while still collapsing a burst of typing into a single
 * write. Anything that is not typing -- a toggle, a delete, a reorder, an
 * agent tool call -- lands within one frame of the action.
 */
const DEBOUNCE_MS = 150

export class StoreSync<TStore> {
  private adapter: StoreAdapter<TStore> | null = null
  private dbId = ""
  private timer: ReturnType<typeof setTimeout> | null = null
  /** Most recent store handed to push(). Only this one is ever sent. */
  private latest: TStore | null = null
  private inFlight = false
  /**
   * Set when a load fails: the in-memory store is then whatever localStorage
   * held, which may be stale or belong to an older session. Pushes are
   * refused until the next successful pull, so a debounced save cannot
   * overwrite the database with unverified data.
   */
  private blocked = false
  private listeners = new Set<(s: SyncStatus) => void>()

  private status: SyncStatus = {
    state: "idle", lastSyncedAt: null, error: null, pending: false,
  }

  configure(adapter: StoreAdapter<TStore> | null, dbId: string): void {
    // A queued push belongs to the previous configuration; dropping it here
    // prevents one team's edits being written under another team's id.
    if (this.timer) { clearTimeout(this.timer); this.timer = null }
    this.latest = null
    this.adapter = adapter
    this.dbId = dbId
    this.blocked = false
    this.setStatus({ state: adapter ? "idle" : "offline", pending: false, error: null })
  }

  get enabled(): boolean { return this.adapter !== null }

  subscribe(fn: (s: SyncStatus) => void): () => void {
    this.listeners.add(fn)
    fn(this.status)
    return () => { this.listeners.delete(fn) }
  }

  private setStatus(patch: Partial<SyncStatus>): void {
    this.status = { ...this.status, ...patch }
    for (const fn of this.listeners) fn(this.status)
  }

  /**
   * Reads the store from the database.
   *
   * The three outcomes are deliberately distinct. "empty" and "failed" both
   * yield no store, but they must not be treated alike: on empty it is
   * correct to seed from whatever is local, whereas on failure the local copy
   * may be arbitrarily stale, and writing it back would overwrite good server
   * data with it. Callers must not save after a failed pull.
   */
  async pull(): Promise<PullResult<TStore>> {
    if (!this.adapter) return { ok: false, error: "No database configured." }
    this.setStatus({ state: "syncing", error: null })
    try {
      const store = await this.adapter.load(this.dbId)
      this.setStatus({ state: "idle", error: null })
      // store === null means the team simply has no rows yet.
      return { ok: true, store }
    } catch (err) {
      const message = describeError(err)
      console.error("[smart-cms] pull failed:", err)
      this.setStatus({ state: "error", error: message })
      return { ok: false, error: message }
    }
  }

  blockWrites(reason: string): void {
    this.blocked = true
    this.setStatus({ state: "error", error: reason, pending: false })
  }

  /** Queues a debounced push. Safe to call on every keystroke. */
  push(store: TStore): void {
    if (!this.adapter) return
    // A failed load leaves the in-memory store untrustworthy; saving it would
    // clobber the database with data we could not verify.
    if (this.blocked) return
    this.latest = store
    this.setStatus({ pending: true })
    if (this.timer) clearTimeout(this.timer)
    this.timer = setTimeout(() => { void this.flush() }, DEBOUNCE_MS)
  }

  /** Writes immediately, bypassing the debounce. Used before unload. */
  async flush(): Promise<void> {
    if (!this.adapter || this.latest === null) return
    if (this.timer) { clearTimeout(this.timer); this.timer = null }

    /*
     * Serialise: two overlapping writes of the same store could apply out of
     * order, and the stale one would silently win. Rather than waiting out
     * another window, the queued write is picked up the moment the in-flight
     * one finishes (see the tail of the try block).
     */
    if (this.inFlight) return

    const store = this.latest
    this.inFlight = true
    this.setStatus({ state: "syncing" })
    try {
      await this.adapter.save(this.dbId, store)
      // Anything queued while this was in flight stays pending.
      const stillPending = this.latest !== store
      this.setStatus({
        state: "idle",
        error: null,
        lastSyncedAt: Date.now(),
        pending: stillPending,
      })
      if (!stillPending) this.latest = null
    } catch (err) {
      const message = describeError(err)
      console.error("[smart-cms] push failed:", err)
      // `latest` is deliberately kept, so a later flush retries this edit.
      this.setStatus({ state: "error", error: message, pending: true })
    } finally {
      this.inFlight = false
      // An edit that arrived mid-write goes out now, not after another window.
      if (this.latest !== null && this.status.state !== "error") {
        void this.flush()
      }
    }
  }

  async clear(): Promise<void> {
    if (!this.adapter) return
    if (this.timer) { clearTimeout(this.timer); this.timer = null }
    this.latest = null
    await this.adapter.clear(this.dbId)
    this.setStatus({ state: "idle", pending: false, error: null, lastSyncedAt: Date.now() })
  }
}
